import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CircularProgress,
  TextField,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { deleteProgramme, programmeListing } from "src/DAL/Programme/Programme";
import { s3baseUrl } from "src/config/config";
import { convertToTitleCase, htmlDecode } from "src/utils/convertHtml";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { filter } from "lodash";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomPopoverSection from "src/components/MenuOption/CustomPopoverSection";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import SaveAsProgrammeDuplicate from "./components/SaveAsProgrammeDuplicate";
import { useContentSetting } from "src/Hooks/ContentSettingState";
import moment from "moment";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) => _user.title.toLowerCase().indexOf(query.toLowerCase()) !== -1,
    );
  }
  return stabilizedThis.map((el) => el[0]);
}
export default function Programmes({ type }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { setProgramTab } = useContentSetting();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [orderBy, setOrderBy] = useState("number");
  const [order, setOrder] = useState("asc");
  const [filterName, setFilterName] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState("");
  const [modalDuplicateOpen, setModalDuplicateOpen] = useState(false);
  const { folder_id } = useParams();
  const { state } = useLocation();

  const [tempData, setTempData] = useState("");

  const {
    data: program = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["programmes", type, folder_id],
    queryFn: async () => {
      const result = await programmeListing(type, folder_id);
      if (result.code === 200) {
        return result.program;
      }
      throw new Error(result.message);
    },
  });

  useEffect(() => {
    setProgramTab(type);
  }, [setProgramTab, type]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  const handleNavigateDetailForms = (value) => {
    navigate(`/programmes/programmes-form/${value._id}`, {
      state: value,
    });
  };

  const handleSaveAsDuplicate = (value) => {
    setModalDuplicateOpen(true);
    setTempData(value);
  };
  const handleNavigateDetail = (value) => {
    navigate(`/programmes/programmes-detail/${value.program_slug}`, {
      state: value,
    });
  };

  const filteredPrograme = applySortFilter(
    program,
    getComparator(order, orderBy),
    filterName.trim(),
  );

  const handleNavigateEdit = (value) => {
    navigate(`/programmes/edit-programme/${value?.program_slug}`, {
      state: value,
    });
  };
  const handleNavigateAdd = (value) => {
    navigate(`/programmes/${"add"}/${value._id}`, {
      state: value,
    });
  };
  const handleNavigateRemove = (value) => {
    navigate(`/programmes/${"remove"}/${value._id}`, {
      state: value,
    });
  };
  const handleNavigateCalendar = (value) => {
    if (value.program_length_type == "limited") {
      navigate(
        `/programmes-limited-calendar/${value?._id}/${value?.program_slug}`,
      );
    } else {
      navigate(`/programmes-calendar/${value?._id}/${value?.program_slug}`);
    }
  };

  const deleteProgrammeMutation = useMutation({
    mutationFn: async (slug) => {
      const result = await deleteProgramme(slug);
      if (result.code !== 200) {
        throw new Error(result.message || "Failed to delete programme");
      }
      return result;
    },
    onSuccess: (result) => {
      handleCloseDelete();
      enqueueSnackbar(result.message, { variant: "success" });
      queryClient.invalidateQueries({
        queryKey: ["programmes", type, folder_id],
      });
    },
    onError: (mutationError) => {
      enqueueSnackbar(mutationError.message, { variant: "error" });
      handleCloseDelete();
    },
  });

  const handleDelete = async () => {
    if (!deleteDoc?.program_slug) return;
    deleteProgrammeMutation.mutate(deleteDoc.program_slug);
  };

  const handleOpenDelete = (value) => {
    setDeleteDoc(value);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => setOpenDelete(false);

  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: "akar-icons:edit",
      handleClick: handleNavigateEdit,
    },

    {
      label: "Details",
      icon: "bx:detail",
      handleClick: handleNavigateDetail,
    },
    {
      label: "Forms",
      icon: "fluent:form-28-regular",
      handleClick: handleNavigateDetailForms,
    },
    {
      label: "Duplicate",
      icon: "radix-icons:copy",
      handleClick: handleSaveAsDuplicate,
    },
    {
      label: "Add Programme Access",
      icon: "ic:round-person-add",
      handleClick: handleNavigateAdd,
    },
    {
      label: "Remove Programme Access",
      icon: "ic:sharp-person-off",
      handleClick: handleNavigateRemove,
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: handleOpenDelete,
    },
  ];

  const MENU_OPTIONS1 = [
    {
      label: "Details",
      icon: "bx:detail",
      handleClick: handleNavigateDetail,
    },
    {
      label: "Forms",
      icon: "fluent:form-28-regular",
      handleClick: handleNavigateDetailForms,
    },
  ];

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  if (isLoading === true) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }
  const isRecordNotFound = filteredPrograme.length === 0;

  return (
    <div className="container mt-2">
      <CustomConfirmation
        handleOpenDelete={handleOpenDelete}
        setOpen={setOpenDelete}
        title={
          <p className="confirmation-string">
            Deleting this Programme will permanently remove all data associated
            with it, including all logs and records added by members. Please
            confirm that you understand the consequences and wish to proceed !
          </p>
        }
        handleCloseDelete={handleCloseDelete}
        handleAgree={handleDelete}
        open={openDelete}
        setOpenDelete={setOpenDelete}
      />
      <SaveAsProgrammeDuplicate
        open={modalDuplicateOpen}
        setOpen={setModalDuplicateOpen}
        data={tempData}
        reload={refetch}
      />
      <div className="row align-items-center">
        <div className="col-lg-6 col-sm-12 d-flex">
          {type == "active" && (
            <>
              <IconButton
                className="back-screen-button"
                onClick={() => {
                  navigate(-1);
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              {state?.name && <h2>{state?.name}</h2>}
            </>
          )}
        </div>
        <div className="col-lg-6 col-sm-12 d-flex justify-content-end">
          <TextField
            fullWidth
            label="Search"
            sx={{ marginRight: 2, width: 240 }}
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
          {type !== "inactive" && (
            <button
              onClick={() => navigate(`/programmes/add-programme/${folder_id}`)}
              className="small-contained-button"
              style={{
                height: "55px",
              }}
            >
              Add Programme
            </button>
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-lg-12 col-sm-12">
          {filteredPrograme.some(
            (item) => item?.action_by !== "coach_user",
          ) && <h2>Shared Programmes </h2>}
        </div>
        {filteredPrograme.map((value, index) => {
          if (value?.action_by !== "coach_user")
            return (
              <div className="col-lg-4 col-md-4 col-sm-12 mb-4" key={index}>
                <div className="card mt-4 pods-cards-shadow cursor h-100 overflow-hidden ">
                  {!value?.status && (
                    <span
                      style={{
                        position: "absolute",
                        top: "36px",
                        justifyContent: "center",
                        zIndex: "1",
                        left: "-38px",
                        transform: "rotate(-53deg)",
                      }}
                    >
                      <Chip
                        label={"Inactive"}
                        variant="contained"
                        color={"error"}
                        sx={{
                          fontSize: "10px",
                          height: "auto",
                          width: "150px",
                        }}
                      />
                    </span>
                  )}
                  <img
                    src={s3baseUrl + value.program_images.thumbnail_1}
                    className="card-img-top pods-image"
                    alt="Programme"
                    onClick={() => handleNavigateCalendar(value)}
                  />
                  <div className="card-body">
                    <div className="row">
                      <div className="col-12">
                        <h3
                          className="h2-heading"
                          onClick={() => handleNavigateCalendar(value)}
                        >
                          {htmlDecode(value.title)}
                        </h3>
                      </div>

                      <div className="col-2 programmes-menu-option">
                        <CustomPopoverSection
                          menu={MENU_OPTIONS1}
                          data={value}
                        />
                      </div>
                    </div>
                    <p
                      className="programme-card-desc mb-3"
                      onClick={() => handleNavigateCalendar(value)}
                    >
                      {htmlDecode(value.short_description)}
                    </p>
                    {value?.review_settings?.general_review_date &&
                    value?.review_settings?.apply_to_all_users ? (
                      <Chip
                        label={`Review Date: ${moment(
                          value?.review_settings?.general_review_date,
                        ).format("DD-MM-YYYY")}`}
                        color="primary"
                        sx={{
                          borderRadius: "5px",
                        }}
                      />
                    ) : (
                      ""
                    )}
                    <div
                      className="row recording-card-date mb-2"
                      onClick={() => handleNavigateCalendar(value)}
                    >
                      <div className="col-5 card-button recording-card-date-position">
                        <p className="pods-active-members">
                          {value?.status && (
                            <span> {value.members} Members</span>
                          )}
                        </p>
                      </div>
                      <div className="col-7 text-end ms-auto pods-active-members text-capital">
                        {`${
                          value.program_type ? value.program_type : "General"
                        } | ${convertToTitleCase(value.program_length_type)}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
        })}
        <div className="col-lg-12 col-sm-12 mt-5">
          {filteredPrograme.some(
            (item) => item?.action_by === "coach_user",
          ) && <h2>Personal Programmes </h2>}
        </div>
        {filteredPrograme.map((value, index) => {
          if (value?.action_by === "coach_user")
            return (
              <div className="col-lg-4 col-md-4 col-sm-12 mb-4" key={index}>
                <div className="card mt-4 pods-cards-shadow cursor h-100 overflow-hidden ">
                  {!value?.status && (
                    <span
                      style={{
                        position: "absolute",
                        top: "36px",
                        justifyContent: "center",
                        zIndex: "1",
                        left: "-38px",
                        transform: "rotate(-53deg)",
                      }}
                    >
                      <Chip
                        label={"Inactive"}
                        variant="contained"
                        color={"error"}
                        sx={{
                          fontSize: "10px",
                          height: "auto",
                          width: "150px",
                        }}
                      />
                    </span>
                  )}
                  <img
                    src={s3baseUrl + value.program_images.thumbnail_1}
                    className="card-img-top pods-image"
                    alt="Programme"
                    onClick={() => handleNavigateCalendar(value)}
                  />
                  <div className="card-body">
                    <div className="row">
                      <div className="col-12">
                        <h3
                          className="h2-heading"
                          onClick={() => handleNavigateCalendar(value)}
                        >
                          {htmlDecode(value.title)}
                        </h3>
                      </div>

                      <div className="col-2 programmes-menu-option">
                        <CustomPopoverSection
                          menu={MENU_OPTIONS}
                          data={value}
                        />
                      </div>
                    </div>
                    <p
                      className="programme-card-desc mb-3"
                      onClick={() => handleNavigateCalendar(value)}
                    >
                      {htmlDecode(value.short_description)}
                    </p>
                    {value?.review_settings?.general_review_date &&
                    value?.review_settings?.apply_to_all_users ? (
                      <Chip
                        label={`Review Date: ${moment(
                          value?.review_settings?.general_review_date,
                        ).format("DD-MM-YYYY")}`}
                        color="primary"
                        sx={{
                          borderRadius: "5px",
                        }}
                      />
                    ) : (
                      ""
                    )}
                    <div
                      className="row recording-card-date mb-2"
                      onClick={() => handleNavigateCalendar(value)}
                    >
                      <div className="col-5 card-button recording-card-date-position">
                        <p className="pods-active-members">
                          {value?.status && (
                            <span> {value.members} Members</span>
                          )}
                        </p>
                      </div>
                      <div className="col-7 text-end ms-auto pods-active-members text-capital">
                        {`${
                          value.program_type ? value.program_type : "General"
                        } | ${convertToTitleCase(value.program_length_type)}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
        })}
        {isRecordNotFound && (
          <Typography
            variant="h5"
            align="center"
            style={{ marginTop: 90 }}
            justifyContent="center"
          >
            No results found
          </Typography>
        )}
      </div>
    </div>
  );
}
